import { useEffect, useState } from "react";
import { adminUrlSources, adminSmokeTestUrls } from "@/lib/api";
import { toast } from "sonner";
import { Link2, ChevronDown, ChevronRight, AlertTriangle, Check, Zap } from "lucide-react";

// Lightweight audit panel for every server-side URL construction. Built
// after v4.73 caught the second cross-environment Stripe redirect bug —
// `PUBLIC_APP_URL` env-var pinning had bitten us in two distinct spots,
// so we need a one-glance overview of which endpoints use which strategy.
//
// Categories:
//   request_dynamic → derive_base_url(request)  · stays correct cross-env
//   client_origin   → frontend supplies origin  · also dynamic
//   email_default   → PUBLIC_APP_URL/BRAND_URL  · intentional (no request)
//   needs_fix       → still hardcodes env var   · MIGRATE THESE
const CATEGORY_META = {
    request_dynamic: {
        label: "Request-dynamic",
        color: "var(--bl-moss)",
        icon: Check,
        explainer: "Uses the inbound request's host. Safe in both envs.",
    },
    client_origin: {
        label: "Client-origin",
        color: "var(--bl-teal)",
        icon: Check,
        explainer: "Frontend sends window.location.origin. Also safe.",
    },
    email_default: {
        label: "Email default",
        color: "var(--bl-muted)",
        icon: Check,
        explainer: "Cron / background — no request to read. Brand URL is intended.",
    },
    needs_fix: {
        label: "Needs fix",
        color: "var(--bl-rust)",
        icon: AlertTriangle,
        explainer: "Still hardcodes PUBLIC_APP_URL inside a request handler. Migrate.",
    },
};

export default function UrlSourcesPanel() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [smoke, setSmoke] = useState(null);
    const [smokeBusy, setSmokeBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminUrlSources();
            setData(r);
        } catch {
            /* non-blocking */
        } finally {
            setLoading(false);
        }
    };

    const runSmoke = async (e) => {
        e?.stopPropagation?.();
        setSmokeBusy(true);
        try {
            const r = await adminSmokeTestUrls();
            setSmoke(r);
            setOpen(true);
            if (r.ok && r.summary?.warn === 0) {
                toast.success("All URL probes passed ✓");
            } else if (r.ok) {
                toast.warning(`Smoke test: ${r.summary?.warn} warning(s), 0 failures`);
            } else {
                toast.error(`Smoke test: ${r.summary?.fail} failure(s) found`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Smoke test failed");
        } finally {
            setSmokeBusy(false);
        }
    };

    useEffect(() => { load(); }, []);

    const summary = data?.summary || {};
    const needsFix = summary.needs_fix || 0;
    const total = Object.values(summary).reduce((a, b) => a + b, 0);

    return (
        <div
            className="bl-card p-5 mt-6"
            data-testid="admin-url-sources-panel"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid="admin-url-sources-toggle"
            >
                <div className="flex items-start gap-3 min-w-0">
                    <Link2 className="w-5 h-5 text-[var(--bl-teal)] mt-0.5 shrink-0" aria-hidden />
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-bl text-lg">
                            URL sources · cross-env audit
                            {needsFix > 0 && (
                                <span className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] uppercase font-semibold bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]">
                                    {needsFix} need fix
                                </span>
                            )}
                        </div>
                        <div className="mt-0.5 text-xs text-bl-muted">
                            {total} server-side URL source{total === 1 ? "" : "s"} mapped.{" "}
                            {needsFix === 0
                                ? "All using the right strategy for their context."
                                : `${needsFix} still hardcode PUBLIC_APP_URL — should migrate to derive_base_url().`}
                        </div>
                        {data?.request_derived_base_url && (
                            <div className="mt-1 text-[11px] text-bl-muted font-mono-bl">
                                Current request resolves to:{" "}
                                <span className="text-bl font-semibold">
                                    {data.request_derived_base_url}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <span className="shrink-0 flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={runSmoke}
                        disabled={smokeBusy}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[var(--bl-teal)] text-white hover:opacity-90 transition-opacity flex items-center gap-1"
                        data-testid="admin-url-sources-smoke-btn"
                    >
                        <Zap className="w-3.5 h-3.5" aria-hidden />
                        {smokeBusy ? "Running…" : "Smoke test"}
                    </button>
                    <span className="text-bl-muted ml-1">
                        {open
                            ? <ChevronDown className="w-4 h-4" aria-hidden />
                            : <ChevronRight className="w-4 h-4" aria-hidden />
                        }
                    </span>
                </span>
            </button>

            {smoke && (
                <div
                    className="mt-4 border rounded-md p-3 bg-bl-bg"
                    data-testid="admin-url-sources-smoke-result"
                    style={{
                        borderColor: smoke.summary?.fail
                            ? "var(--bl-rust)"
                            : smoke.summary?.warn
                                ? "var(--bl-amber, #C29A45)"
                                : "var(--bl-moss)",
                    }}
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <span
                            className="px-2 py-0.5 rounded text-[9px] uppercase font-display font-semibold"
                            style={{
                                background: smoke.summary?.fail
                                    ? "var(--bl-rust-tint)"
                                    : smoke.summary?.warn
                                        ? "var(--bl-amber-tint, #FAF1DC)"
                                        : "var(--bl-moss-tint)",
                                color: smoke.summary?.fail
                                    ? "var(--bl-rust)"
                                    : smoke.summary?.warn
                                        ? "var(--bl-amber, #8E6A1F)"
                                        : "var(--bl-moss)",
                            }}
                        >
                            {smoke.summary?.fail
                                ? `${smoke.summary.fail} FAIL`
                                : smoke.summary?.warn
                                    ? `${smoke.summary.warn} WARN`
                                    : "ALL PASS"}
                        </span>
                        <span className="text-xs text-bl-muted">
                            Request resolved to{" "}
                            <span className="font-mono-bl text-bl">{smoke.current_host}</span>
                        </span>
                    </div>
                    <ul className="mt-2 space-y-1" data-testid="admin-url-sources-smoke-list">
                        {(smoke.checks || []).map((c, i) => {
                            const color = c.status === "pass"
                                ? "var(--bl-moss)"
                                : c.status === "warn"
                                    ? "var(--bl-amber, #8E6A1F)"
                                    : "var(--bl-rust)";
                            const Icon = c.status === "pass" ? Check : AlertTriangle;
                            return (
                                <li key={i} className="text-[11px] flex items-start gap-2">
                                    <Icon
                                        className="w-3.5 h-3.5 mt-0.5 shrink-0"
                                        style={{ color }}
                                        aria-hidden
                                    />
                                    <div className="min-w-0">
                                        <div className="font-display font-semibold text-bl">
                                            {c.name}
                                        </div>
                                        <div className="font-mono-bl text-bl-muted truncate">
                                            {c.value || "(empty)"}
                                        </div>
                                        <div className="text-bl-muted italic">{c.detail}</div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {open && data && (
                <div
                    className="mt-4 space-y-4"
                    data-testid="admin-url-sources-body"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(CATEGORY_META).map(([key, meta]) => {
                            const n = summary[key] || 0;
                            const Icon = meta.icon;
                            return (
                                <div
                                    key={key}
                                    className="border border-bl rounded-md p-3 bg-bl-surface"
                                    data-testid={`url-sources-tile-${key}`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <Icon
                                            className="w-3.5 h-3.5"
                                            style={{ color: meta.color }}
                                            aria-hidden
                                        />
                                        <span className="text-[10px] tracking-[0.12em] uppercase font-semibold text-bl-muted">
                                            {meta.label}
                                        </span>
                                    </div>
                                    <div
                                        className="mt-1 text-2xl font-display font-bold tabular-nums"
                                        style={{ color: meta.color }}
                                    >
                                        {n}
                                    </div>
                                    <div className="text-[11px] text-bl-muted mt-0.5 leading-snug">
                                        {meta.explainer}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="overflow-x-auto" data-testid="admin-url-sources-table">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] tracking-[0.16em] uppercase text-bl-muted">
                                    <th className="text-left py-2 px-2 font-medium">Category</th>
                                    <th className="text-left py-2 px-2 font-medium">File</th>
                                    <th className="text-left py-2 px-2 font-medium">Endpoint</th>
                                    <th className="text-left py-2 px-2 font-medium">Field / Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.audit || []).map((row, idx) => {
                                    const meta = CATEGORY_META[row.category] || {};
                                    return (
                                        <tr key={idx} className="border-t border-bl-line">
                                            <td className="py-2 px-2">
                                                <span
                                                    className="px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold"
                                                    style={{
                                                        background: row.category === "needs_fix"
                                                            ? "var(--bl-rust-tint)"
                                                            : "var(--bl-moss-tint)",
                                                        color: meta.color || "var(--bl-bl)",
                                                    }}
                                                >
                                                    {(meta.label || row.category)}
                                                </span>
                                            </td>
                                            <td className="py-2 px-2 font-mono-bl text-bl">
                                                {row.file}
                                            </td>
                                            <td className="py-2 px-2 font-mono-bl text-bl">
                                                {row.endpoint}
                                            </td>
                                            <td className="py-2 px-2 text-bl-muted">
                                                <div>{row.field}</div>
                                                {row.note && (
                                                    <div className="mt-0.5 text-[10px] italic">
                                                        {row.note}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[11px] text-bl-muted bg-bl-bg border border-bl rounded p-3 font-mono-bl">
                        <div>
                            <b>PUBLIC_APP_URL</b> env ={" "}
                            <span className="text-bl">{data.public_app_url_env || "—"}</span>
                        </div>
                        <div>
                            <b>PUBLIC_BRAND_URL</b> env ={" "}
                            <span className="text-bl">{data.public_brand_url_env || "—"}</span>
                        </div>
                    </div>
                </div>
            )}

            {open && loading && !data && (
                <div className="mt-3 text-center text-sm text-bl-muted py-4">Loading…</div>
            )}
        </div>
    );
}
